import React from 'react';
import { AppErrorScreen } from '~/components';
import PaginationButton from '~/components/DataTables/components/PaginationButton';
import Icon from '~/components/Icon';
import useInfinitePagination from '~/components/util/useInfinitePagination';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import cleanedFilename from '~/util/cleanedFilename';
import composeInvoiceListData from '../../util/composeInvoiceListData';
import invoiceListColumns from '../../util/invoiceListColumns';
import InvoiceListTable from '../InvoiceListTable';
import PaginationContainer from '../PaginationContainer';
import TEST_ID from './index.testid';
import { useGetInvoicesQuery } from '~/graphql/types';

type Props = {
  limit: number;
};
const StandardInvoices: React.FC<Props> = ({ limit }) => {
  const account = useCurrentAccount();
  const [currentPage, goToPreviousPage, goToNextPage, grabItems, knownPages] =
    useInfinitePagination(limit);

  const variables = { accountId: account.id, limit };

  const { data, loading, networkStatus, fetchMore, error } =
    useGetInvoicesQuery({
      variables,
      notifyOnNetworkStatusChange: true,
    });

  const invoices = data?.getInvoices?.items;

  if (!loading && (error || !invoices)) {
    return <AppErrorScreen />;
  }

  const nextToken = data?.getInvoices?.nextToken;

  const loadMore = () =>
    fetchMore({
      variables: {
        ...variables,
        nextToken: nextToken || null,
      },
    });

  return (
    <>
      <InvoiceListTable
        loading={loading}
        networkStatus={networkStatus}
        columns={invoiceListColumns()}
        data={composeInvoiceListData(grabItems(invoices), variables)}
      />
      <PaginationContainer>
        <PaginationButton
          disabled={knownPages === currentPage && nextToken == null}
          chevronItem
          data-testid={TEST_ID.GO_TO_NEXT_BUTTON}
          onClick={() => {
            if (knownPages === currentPage && nextToken != null) {
              loadMore()
                .then(() => {
                  goToNextPage();
                })
                .catch(e => {
                  throw Error(`${cleanedFilename(__filename)} | Error ${e}`);
                });
            } else {
              goToNextPage();
            }
          }}
        >
          <Icon name="chevron" clockwise={-90} />
        </PaginationButton>
        <PaginationButton
          disabled={currentPage === 1}
          chevronItem
          data-testid={TEST_ID.GO_TO_PREVIOUS_BUTTON}
          onClick={() => {
            goToPreviousPage();
          }}
        >
          <Icon name="chevron" clockwise={90} />
        </PaginationButton>
      </PaginationContainer>
    </>
  );
};

export default StandardInvoices;
